// @flow

import moment from 'moment';
require('moment/locale/fr');
require('moment/locale/de');
import _ from 'lodash';

import ArrayUtils from './ArrayUtils';
import I18n from '../i18n/I18n';

import ApiConstants from '../constants/ApiConstants';

import Timelapse from '../entities/Timelapse';

export default class DateUtils {

  /**
   * @deprecated
   */
  static MINUTE_AS_MILLISECONDS = 60 * 1000;

  /**
   * @deprecated
   */
  static HOUR_AS_MILLISECONDS = 3600 * 1000;

  /**
   * @deprecated
   */
  static DAY_AS_MILLISECONDS = 86400 * 1000;

  static MINUTE_AS_MS = 60 * 1000;
  static HOUR_AS_MS = 3600 * 1000;
  static DAY_AS_MS = 86400 * 1000;

  // -- formats
  static DMY_FORMAT = 'L';
  static SECOND_FORMAT = 'X';
  static MS_FORMAT = 'x';

  //
  static BEGIN_DAY_TIEMSTAMP = 0;
  static END_DAY_TIMESTAMP = DateUtils.DAY_AS_MILLISECONDS;

  /**
   * Take a Moment or an MsTimestamp and return a moment
   * @type {[type]}
   */
  static anyToDate(date: any): Moment {
    if (_.isNumber(date)) {
      return this.fromMsTimestamp(date);
    }

    return moment(date); // copy in order to not change the reference data.
  }

  /**
   * Return a moment instance for the given timestamp.
   * @param timestamp
   * @param format Define if the timestamp is in second or milliseconds.
   * @returns moment instance.
   */
  static fromTimestamp(timestamp: number,
                       format: string = DateUtils.SECOND_FORMAT): Object {

    return moment(timestamp, format).locale(I18n.getCurrentLanguageWithoutRegionCode());
  }

  static fromMsTimestamp(timestamp: number): Object {
    return moment(timestamp, DateUtils.MS_FORMAT).locale(I18n.getCurrentLanguageWithoutRegionCode());
  }

  static stringDateWithFormatToUnix(stringDate: string, format: string): number {
    return moment(stringDate, format).locale(I18n.getCurrentLanguageWithoutRegionCode()).unix();
  }

  static stringDateWithFormatToMs(stringDate: string, format: string): number {
    return moment(stringDate, format).locale(I18n.getCurrentLanguageWithoutRegionCode()).valueOf();
  }

  static getTimestampAsMilliseconds(date) {
    return moment(date).locale(I18n.getCurrentLanguageWithoutRegionCode()).valueOf();
  }

  static fromSecondsTimestampToString(timestamp: number,
                                      format: string = 'DD/MM/Y'): string {
    return moment(timestamp, DateUtils.SECOND_FORMAT).locale(I18n.getCurrentLanguageWithoutRegionCode()).format(format);

  }

  static getTodayTimestampAsSeconds(): number {
    return moment().unix();
  }

  static getTodayTimestampAsMilliseconds(): number {
    return moment().unix() * 1000;
  }

  static getTimelapseDayInterval(): Timelapse {
    const timelapse:Timelapse = new Timelapse();

    const fromTime = moment().startOf('day');
    const toTime = moment().endOf('day');

    timelapse.setFromTime(fromTime.unix());
    timelapse.setToTime(toTime.unix());

    return timelapse;
  }

  static getTimelapseWeekInterval(): Timelapse {
    const timelapse:Timelapse = new Timelapse();

    const fromTime = moment().subtract(1, 'weeks').startOf('day');
    const toTime = moment().endOf('day');

    timelapse.setFromTime(fromTime.unix());
    timelapse.setToTime(toTime.unix());

    return timelapse;
  }

  static getTimelapseWeekIntervalInFuture(): Timelapse {
    const timelapse:Timelapse = new Timelapse();

    const fromTime = moment().startOf('day');
    const toTime = moment().add(1, 'weeks').endOf('day');

    timelapse.setFromTime(fromTime.unix());
    timelapse.setToTime(toTime.unix());

    return timelapse;
  }

  static getTimelapseMonthInterval(): Timelapse {
    const timelapse:Timelapse = new Timelapse();

    const fromTime = moment().subtract(1, 'months').startOf('day');
    const toTime = moment().endOf('day');

    timelapse.setFromTime(fromTime.unix());
    timelapse.setToTime(toTime.unix());

    return timelapse;
  }

  static getTimelapseMonthIntervalInFuture(): Timelapse {
    const timelapse:Timelapse = new Timelapse();

    const fromTime = moment().startOf('day');
    const toTime = moment().add(1, 'months').endOf('day');

    timelapse.setFromTime(fromTime.unix());
    timelapse.setToTime(toTime.unix());

    return timelapse;
  }

  static millisecondsToMinutes(timestamp: number): number {
    return Math.round(timestamp / this.MINUTE_AS_MILLISECONDS);
  }

  static millisecondsToDays(timestamp: number): number {
    return Math.round(timestamp / this.DAY_AS_MILLISECONDS);
  }

  static minutesToMilliseconds(minutes: number): number {
    return minutes * this.MINUTE_AS_MILLISECONDS;
  }

  static daysToMilliseconds(days: number): number {
    return days * this.DAY_AS_MILLISECONDS;
  }

  /**
   * Get a timestamp and return it with only hour and minutes in seconds.
   * Note that it does not handle timezones :/
   * @param value
   * @param timestampFormat
   * @param returnFormat default same as timestamp format.
   * @returns {number}
   */
  static getTimestampForHour(value: number,
                             timestampFormat: string = DateUtils.SECOND_FORMAT,
                             returnFormat: ?string = null): number {
    if (value.toString().length > 11
      && timestampFormat === DateUtils.SECOND_FORMAT) {
      console.error('time set to ms', value);
    }
    const date = moment(value, timestampFormat);

    const timestampAsSeconds = this.getTimestampForHourWithDate(date) / 1000;

    let format = returnFormat;
    if (_.isNull(format)) {
      format = timestampFormat;
    }
    if (format === DateUtils.SECOND_FORMAT) {
      return timestampAsSeconds;
    }
    // DateUtils.MS_FORMAT
    return timestampAsSeconds * 1000;
  }

  static getTimestampForHourWithDate(dateParam: Object): number {
    const date = this.anyToDate(dateParam);
    return ((date.hour() * 3600) + (date.minute() * 60)) * 1000;
  }

  /**
   * Return a timestamp as seconds with the given hour and minute.
   * @param hour
   * @param minute
   * @returns {number}
   */
  static getTimestampForHourAndMinute(hour: number, minute: number): number {
    return ((hour * 3600) + (minute * 60));
  }

  static getMsTimestampForHourAndMinute(hour: number, minute: number): number {
    return ((hour * 3600) + (minute * 60)) * 1000;
  }

  static isHour(timestamp: number): boolean {
    return timestamp <= DateUtils.DAY_AS_MILLISECONDS;
  }

  static getHourFromMsHourTimestamp(date: number): number {
    return parseInt(date / 1000 / 3600, 10);
  }

  static getMinuteFromMsHourTimestamp(date: number): number {
    return parseInt(((date / 1000) % 3600) / 60, 10);
  }

  static getHourAndMinuteFromMsTimestamp(date: number): Object {
    return {
      hour: DateUtils.getHourFromMsHourTimestamp(date),
      minute: DateUtils.getMinuteFromMsHourTimestamp(date),
    }
  }

  static getSlotFromDate(dateParam: any): Slot {
    const date = this.anyToDate(dateParam);
    return this.getMsTimestampForHourAndMinute(date.hour(), date.minute());
  }

  static formatTwoDigits(number: number): string {
    return number > 9 ? `${number}` : `0${number}`;
  }

  static getFormattedHourWithMsHour(date): string {
    return `${DateUtils.formatTwoDigits(DateUtils.getHourFromMsHourTimestamp(date))}:${DateUtils.formatTwoDigits(DateUtils.getMinuteFromMsHourTimestamp(date))}`
  }

  static differenceBetweenDatesInDays(date1, date2): number {
    return this.anyToDate(date1).diff(this.anyToDate(date2), 'days');
  }

  static differenceBetweenDatesInMinutes(date1: Moment, date2: Moment): number {
    return this.anyToDate(date1).diff(this.anyToDate(date2), 'minutes');
  }

  /**
   * @param date1 Moment|number|MsTimestamp
   * @type {[type]}
   */
  static isSameDay(date1Param: any, date2Param: any): boolean {
    if (_.isNil(date1Param) || _.isNil(date1Param)) {
      return false;
    }

    const date1 = DateUtils.anyToDate(date1Param);
    const date2 = DateUtils.anyToDate(date2Param);

    return date1.isSame(date2, 'day')
      && date1.isSame(date2, 'month')
      && date1.isSame(date2, 'year');
  }

  static isSameDayWithTimestamp(tm1: number, tm2: number): boolean {
    const date1 = this.fromMsTimestamp(tm1);
    const date2 = this.fromMsTimestamp(tm2);
    return date1.isSame(date2, 'day')
      && date1.isSame(date2, 'month')
      && date1.isSame(date2, 'year');
  }

  /**
   * @param date Moment|number|MsTimestamp
   */
  static isToday(dateParam: any): boolean {
    let date = this.anyToDate(dateParam);
    return this.isSameDay(date, moment());
  }

  static isTomorrow(dateParam: any): boolean {
    let date = this.anyToDate(dateParam);

    const tomorrow = moment().add(1, 'day');
    return tomorrow.isSame(date, 'day')
      && tomorrow.isSame(date, 'month')
      && tomorrow.isSame(date, 'year');
  }

  static getDateString(dateParam: any): string {
    const date = this.anyToDate(dateParam);
    if (this.isToday(date)) {
      return 'Aujourd\'hui';
    }
    if (this.isTomorrow(date)) {
      return 'Demain';
    }

    return date.format('DD/MM');
  }

  static getMonthWeeks(year, month) {
    const dates = DateUtils.getMonthDateRange(year, month);

    if (_.isEmpty(dates)) {
      return [];
    }

    const weeks = [];
    const firstWeekYear = dates[0].week();

    _.each(dates, (date) => {
      // get week index from 0
      let weekIndex = date.week() - firstWeekYear;

      // handle January
      if (weekIndex < 0) {
        weekIndex = date.week();
      }

      if (_.isUndefined(weeks[weekIndex])) {
        weeks[weekIndex] = ArrayUtils.createArrayOfNull(7);
      }

      weeks[weekIndex][date.weekday()] = date;
    });


    return weeks;
  }

  /**
   * @param month the month. January is 0.
   */
  static getMonthDateRange(year, month) {
    const startDate = moment([year, month]);

    // Clone the value before .endOf()
    const endDate = moment(startDate).endOf('month');

    const dates = [];
    let date = moment(startDate);

    while (date.isBefore(endDate)) {
      dates.push(moment(date));
      date = date.add(1, 'day');
    }

    return dates;
  }

  /**
   * moment.js .day() use sunday as first day.
   * We use monday as first day on the API.
   * Use this function to convert.
   *
   * @link http://momentjs.com/docs/#/get-set/iso-weekday/
   */
  static getPmtDayIndex(dateParam: any) {
    const date = this.anyToDate(dateParam);

    // In moment: 7 is Monday or Sunday, depending of the iso weekday
    // If the locale assigns Monday as the first day of the week, moment().weekday(0) will be Monday. If Sunday is the first day of the week, moment().weekday(0) will be Sunday.

    /**
     * isoWeekday: Gets or sets the ISO day of the week with 1 being Monday and 7 being Sunday.
     * day: This method can be used to set the day of the week, with Sunday as 0 and Saturday as 6.
     */
    const day = moment(date).day();

    const daysMomentToPmt = {
      '0': ApiConstants.Day.SUNDAY,
      '1': ApiConstants.Day.MONDAY,
      '2': ApiConstants.Day.TUESDAY,
      '3': ApiConstants.Day.WEDNESDAY,
      '4': ApiConstants.Day.THURSDAY,
      '5': ApiConstants.Day.FRIDAY,
      '6': ApiConstants.Day.SATURDAY,
    };

    return daysMomentToPmt[day];
  }

  static toStartOfDay(dateParam: any) {
    const date = this.anyToDate(dateParam);
    return moment(date).hour(0).minute(0).second(0).millisecond(0);
  }

  /**
   * get number of weekdays between two timestamp
   */
  static nbWeekdaysBetween(d1, d2, isoWeekday) {
    d1 = moment(d1)
    d2 = moment(d2)

    var daysToAdd = ((7 + isoWeekday) - d1.isoWeekday()) % 7

    var nextDay = d1.clone().add(daysToAdd, 'days');
    if (nextDay.isAfter(d2)) {
        return 0;
    }

    var weeksBetween = d2.diff(nextDay, 'weeks');

    return weeksBetween + 1;
  }

}


// Format string
/*
 Month
 -----------------------------
 M: 1 2 ... 11 12
 Mo: 1st 2nd ... 11th 12th
 MM: 01 02 ... 11 12
 MMM Jan Feb ... Nov Dec
 MMMM: January February ... November December

 Day of Month
 -----------------------------
 D: 1 2 ... 30 31
 Do: 1st 2nd ... 30th 31st
 DD: 01 02 ... 30 31
 Day of Year DDD 1 2 ... 364 365
 DDDo: 1st 2nd ... 364th 365th
 DDDD: 001 002 ... 364 365

 Day of Week
 -----------------------------
 d: 0 1 ... 5 6
 do: 0th 1st ... 5th 6th
 dd: Su Mo ... Fr Sa
 ddd: Sun Mon ... Fri Sat
 dddd: Sunday Monday ... Friday Saturday
 Day of Week (Locale): e 0 1 ... 5 6
 Day of Week (ISO): E 1 2 ... 6 7

 Week of Year
 -----------------------------
 w: 1 2 ... 52 53
 wo: 1st 2nd ... 52nd 53rd
 ww: 01 02 ... 52 53

 Week of Year (ISO)
 -----------------------------
 W: 1 2 ... 52 53
 Wo: 1st 2nd ... 52nd 53rd
 WW: 01 02 ... 52 53

 Year
 -----------------------------
 YY: 70 71 ... 29 30
 YYYY: 1970 1971 ... 2029 2030

 Week Year
 -----------------------------
 gg: 70 71 ... 29 30
 gggg: 1970 1971 ... 2029 2030

 Week Year (ISO)
 -----------------------------
 GG: 70 71 ... 29 30
 GGGG: 1970 1971 ... 2029 2030

 AM/PM
 -----------------------------
 A: AM PM
 a: am pm

 Hour:
 -----------------------------
 H: 0 1 ... 22 23
 HH: 00 01 ... 22 23
 h: 1 2 ... 11 12
 hh: 01 02 ... 11 12

 Minute
 -----------------------------
 m: 0 1 ... 58 59
 mm: 00 01 ... 58 59

 Second
 -----------------------------
 s: 0 1 ... 58 59
 ss: 00 01 ... 58 59

 Fractional Second
 -----------------------------
 S: 0 1 ... 8 9
 SS: 0 1 ... 98 99
 SSS 0 1 ... 998 999

 Timezone
 -----------------------------
 z or zz: EST CST ... MST PST
 Z: -07:00 -06:00 ... +06:00 +07:00
 ZZ: -0700 -0600 ... +0600 +0700

 Unix Timestamp
 -----------------------------
 X: 1360013296
 */
